import type { SessionType, TokenStorageAdapter } from "./tokenStorage";

export type { SessionType };

export interface TokenExchangeRequest {
  email: string;
  password: string;
}

export interface TokenExchangeResponse {
  accessToken: string;
  refreshToken: string | null;
  expiresIn: number | null;
}

export interface TokenRefreshResponse {
  accessToken: string;
  refreshToken: string | null;
  expiresIn: number;
}

export type AuthErrorCode =
  | "invalid_credentials"
  | "no_business_unit"
  | "invalid_request"
  | "internal_error"
  | "invalid_token";

export interface AuthErrorResponse {
  code: AuthErrorCode;
  dev_help?: unknown;
}

export class AuthError extends Error {
  code: AuthErrorCode;
  statusCode: number;
  devHelp?: unknown;

  constructor(code: AuthErrorCode, statusCode: number, message?: string, devHelp?: unknown) {
    super(message ?? code);
    this.name = "AuthError";
    this.code = code;
    this.statusCode = statusCode;
    this.devHelp = devHelp;
  }
}

export interface AuthClientConfig {
  /**
   * Base URL for the auth endpoints. Pass a function for environments where
   * the correct host can change at runtime (e.g. country-based routing on
   * mobile); the function is invoked on every request.
   */
  baseUrl: string | (() => string);
  storage?: TokenStorageAdapter;
  onSessionExpired?: () => void;
  // Inlined to avoid pulling in the DOM `lib` — React Native's tsconfig
  // doesn't declare `RequestCredentials`.
  credentials?: "omit" | "same-origin" | "include";
  /**
   * Optional refresh token override. On web, the refresh token lives in an
   * HttpOnly cookie and is not accessible to the auth client. On mobile, the
   * token storage adapter provides the refresh token via the request body.
   */
  getRefreshTokenForBody?: () => Promise<string | null>;
  /**
   * Synchronously seed the client with a persisted access token and its
   * session type at construction time. This avoids a race between the async
   * `hydrate()` and the first outbound request after a page refresh — on web
   * the legacy token lives in `localStorage` which is readable synchronously.
   */
  initialAccessToken?: string | null;
  initialSessionType?: SessionType | null;
}

export interface AuthClient {
  signIn(email: string, password: string): Promise<TokenExchangeResponse>;
  /**
   * Exchange a short-lived nonce JWT (e.g. from a magic-link flow) for a
   * session via `POST /nonce-exchange/`. Uses the same response shape as
   * `signIn` — Cognito users get a refreshable session, legacy users get
   * a non-refreshable one. Supersedes the GraphQL `exchangeNonceToken`
   * mutation, which can't set the session type or wire refresh tokens
   * into the shared auth client.
   */
  nonceExchange(token: string): Promise<TokenExchangeResponse>;
  refresh(): Promise<string | null>;
  getAccessToken(): string | null;
  hydrate(): Promise<void>;
  getSessionType(): SessionType | null;
  logout(): Promise<void>;
}
